import { fmtData } from '../utils';
import type { PrazoDetalhe } from '../types';

/**
 * Linha do tempo "Detalhes do Expediente".
 *
 * Mostra a CADEIA de contagem do prazo de forma auditável, com as datas
 * CORRETAS vindas do backend (prazos.py):
 *   Disponibilização → Publicação (1º dia útil) → Início (dia 1) → Prazo Final.
 *
 * ⚠️ Regime-aware: para Diário Eletrônico (DJEN) NÃO existe "ciência automática"
 * — a contagem corre da publicação. Só rotulamos "ciência" se o meio NÃO for
 * Diário (regime de Domicílio/caixa postal), e nesse caso sinalizamos revisão.
 */

function regimeDiario(meio: string | null): boolean {
  const m = (meio ?? '').trim().toUpperCase();
  return m === '' || m === 'D' || m.startsWith('DIAR') || m.startsWith('DIÁR');
}

function meioLabel(meio: string | null): string {
  if (regimeDiario(meio)) return 'Diário de Justiça Eletrônico (DJEN)';
  return `Meio: ${meio}`;
}

/** Extrai "DESTINATÁRIO(S): X" do teor (mesmo critério do classificador.py). */
function parseDestinatario(teor: string | null): string | null {
  if (!teor) return null;
  const m = teor.match(
    /DESTINAT[ÁA]RIO\(?S?\)?\s*:?\s*(.+?)(?: |\s{2,}|<br|\bFica\b|INTIMA|NOTIFICA|Expediente|notificad|intimad|$)/i,
  );
  return m ? m[1].trim() : null;
}

interface StepProps {
  data: string | null;
  titulo: string;
  cor: string; // classe tailwind do ponto, ex 'bg-sky-500'
  children?: React.ReactNode;
  destaque?: boolean;
}

function Step({ data, titulo, cor, children, destaque }: StepProps) {
  return (
    <div className="relative flex gap-3">
      {/* ponto na linha */}
      <span
        className={`absolute -left-[20.5px] top-1.5 h-2.5 w-2.5 rounded-full border border-white dark:border-slate-900 ${cor}`}
      />
      {/* chip de data */}
      <div className="shrink-0 w-[78px] pt-0.5">
        <span className="inline-block rounded-md bg-sky-50 dark:bg-sky-950/40 px-1.5 py-0.5 text-[10px] font-mono font-semibold text-sky-700 dark:text-sky-300">
          {data ? fmtData(data) : '—'}
        </span>
      </div>
      {/* conteúdo */}
      <div className={`flex-1 pb-0.5 ${destaque ? 'font-semibold' : ''}`}>
        <div className="text-[13px] text-slate-800 dark:text-slate-200">{titulo}</div>
        {children && (
          <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 space-y-0.5">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

export function TimelineExpediente({ c }: { c: PrazoDetalhe }) {
  const diario = regimeDiario(c.meio);
  const destinatario = parseDestinatario(c.teor_resumo);
  const venceISO = c.data_evento ?? c.data_vencimento; // audiência tem data própria
  const ehAudiencia = !!c.data_evento;

  return (
    <div className="bg-slate-50/50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800/80 rounded-xl p-4 shadow-sm backdrop-blur-md">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
          <span>📋</span> Detalhes do Expediente
        </h4>
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-950/50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
          <span>🕑</span>
          {ehAudiencia ? 'Audiência' : 'Prazo Final'}: {venceISO ? fmtData(venceISO) : '—'}
        </span>
      </div>

      {!diario && (
        <div className="mb-3 rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 px-2.5 py-1.5 text-[11px] text-amber-800 dark:text-amber-300">
          ⚠️ Meio não-Diário (<span className="font-mono">{c.meio}</span>) — regime de
          contagem a conferir manualmente.
        </div>
      )}

      <div className="relative border-l border-slate-200 dark:border-slate-800 ml-1.5 pl-4 space-y-3.5 py-1">
        {/* 1) Disponibilização */}
        <Step data={c.data_disponibilizacao} titulo="Intimação / Disponibilização" cor="bg-sky-500">
          <div>Meio de expedição: {meioLabel(c.meio)}</div>
          {destinatario && <div>Destinatário: {destinatario}</div>}
          {c.link_documento && (
            <a
              href={c.link_documento}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 mt-1 rounded-md bg-sky-600 hover:bg-sky-700 text-white px-2 py-0.5 text-[11px] font-medium transition-colors"
            >
              👁 Visualizar ato
            </a>
          )}
        </Step>

        {/* 2) Publicação (regime Diário) ou Ciência (não-Diário) */}
        {c.data_publicacao && (
          <Step
            data={c.data_publicacao}
            titulo={diario ? 'Publicação' : 'Ciência'}
            cor="bg-slate-400 dark:bg-slate-600"
          >
            <div>
              {diario
                ? '1º dia útil seguinte à disponibilização (art. 224 §2º CPC)'
                : 'Ciência da comunicação'}
            </div>
          </Step>
        )}

        {/* 3) Início da contagem */}
        <Step data={c.data_inicio} titulo="Início da contagem" cor="bg-indigo-500">
          <div>
            1º dia útil seguinte à publicação — dia 1 de {c.dias_uteis} dias úteis
          </div>
        </Step>

        {/* 4) Prazo final / Audiência */}
        <Step
          data={venceISO}
          titulo={ehAudiencia ? `Audiência${c.hora_evento ? ` às ${c.hora_evento}` : ''}` : 'Prazo Final'}
          cor="bg-emerald-500"
          destaque
        >
          {!ehAudiencia && <div>{c.dias_uteis} dias úteis (CLT art. 775)</div>}
        </Step>

        {/* 5) Estado da resposta */}
        {c.protocolado_em ? (
          <Step data={c.protocolado_em.slice(0, 10)} titulo="Protocolado" cor="bg-emerald-600">
            <div>{c.protocolado_por?.nome ? `por ${c.protocolado_por.nome}` : 'concluído'}</div>
          </Step>
        ) : (
          <div className="relative flex gap-3">
            <span className="absolute -left-[20.5px] top-1.5 h-2.5 w-2.5 rounded-full border border-white dark:border-slate-900 bg-rose-500" />
            <div className="shrink-0 w-[78px]" />
            <div className="flex-1">
              <span className="inline-block rounded-full bg-rose-100 dark:bg-rose-950/50 px-2.5 py-0.5 text-[11px] font-semibold text-rose-700 dark:text-rose-300">
                Sem Resposta
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
